import { Button, Grid, message, Modal, Typography } from "antd";
import { X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { useAuthStore } from "@/store/auth.store";
import { Logout } from "@/services/auth.service";
import { wait } from "@/utils/wait";

const { Title } = Typography;
const { useBreakpoint } = Grid;

type SignOutProps = {
  open: boolean;
  onClose: () => void;
};

function ModalTitle({ onClose }: { onClose: () => void }) {
  const screens = useBreakpoint();

  return (
    <div className="flex items-center justify-between gap-4 bg-[#E5F2FF] p-6 rounded-tl-2xl rounded-tr-2xl">
      <Title
        level={2}
        style={{
          marginBottom: 0,
          fontSize: screens.xs ? 24 : 28,
          fontWeight: 700,
        }}
      >
        Log Keluar
      </Title>
      <button type="button" className="cursor-pointer" onClick={onClose}>
        <X size={24} aria-hidden="true" />
      </button>
    </div>
  );
}

function ModalButton({
  onClose,
  onSubmit,
  isSubmitting,
}: {
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}) {
  return (
    <div className="grid md:grid-cols-2 md:gap-10 gap-6">
      <Button
        htmlType="button"
        color="default"
        variant="solid"
        size="large"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Batal
      </Button>
      <Button
        type="primary"
        size="large"
        onClick={onSubmit}
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        Log keluar
      </Button>
    </div>
  );
}

export function SignOut({ open, onClose }: SignOutProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignOut = async () => {
    if (isSubmitting) return;

    const startTime = Date.now();

    try {
      setIsSubmitting(true);

      const adminId = useAuthStore.getState().user?.adminId;

      if (!adminId) {
        throw new Error("Admin tidak dijumpai");
      }
      await Logout({
        adminId,
      });

      // Keep loading for at least 500ms
      const elapsed = Date.now() - startTime;
      const minimumLoadingDuration = 500;
      if (elapsed < minimumLoadingDuration) {
        await wait(minimumLoadingDuration - elapsed);
      }
      useAuthStore.getState().logout();

      onClose();

      await wait(500);

      navigate(ROUTES.adminLogin, { replace: true });
    } catch (error) {
      console.error("Log keluar gagal:", error);
      message.error(
        error instanceof Error ? error.message : "Log keluar gagal",
      );

      useAuthStore.getState().logout();

      onClose();
      navigate(ROUTES.adminLogin, {
        replace: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      closable={false}
      title={<ModalTitle onClose={onClose} />}
      styles={{
        container: { padding: 0 },
        header: { padding: 0, marginBottom: 0 },
        body: { padding: 24 },
        footer: { padding: "0 16px 16px" },
      }}
      width={900}
      footer={
        <ModalButton
          onClose={onClose}
          onSubmit={handleSignOut}
          isSubmitting={isSubmitting}
        />
      }
      centered
    >
      <Title
        level={4}
        style={{ textAlign: "center", fontSize: 24, fontWeight: 700 }}
      >
        Adakah anda pasti ingin log keluar?
      </Title>
    </Modal>
  );
}
